import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import type { AgentDef } from "./sentinel-constants";

export interface AgentRunResult {
  agent: AgentDef;
  status: "running" | "success" | "error";
  data?: unknown;
  error?: string;
  startedAt: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: AgentRunResult | null;
}

export function AgentResultDrawer({ open, onOpenChange, result }: Props) {
  if (!result) return null;
  const Icon = result.agent.icon;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${result.agent.color}`} aria-hidden="true" />
            {result.agent.name}
            {result.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {result.status === "success" && <CheckCircle2 className="w-4 h-4 text-success" />}
            {result.status === "error" && <XCircle className="w-4 h-4 text-destructive" />}
          </SheetTitle>
          <SheetDescription>{result.agent.desc}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">{result.agent.fn}</Badge>
            <span>·</span>
            <span>{new Date(result.startedAt).toLocaleString("pt-BR")}</span>
          </div>

          {result.status === "running" && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Executando agente...
            </div>
          )}

          {result.status === "error" && (
            <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {result.error || "Falha desconhecida"}
            </div>
          )}

          {result.status === "success" && result.data !== undefined && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Resultado</p>
              <pre className="text-[11px] bg-muted p-3 rounded overflow-x-auto max-h-96 whitespace-pre-wrap break-words">
                {typeof result.data === "string"
                  ? result.data
                  : JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
